import React, {useCallback} from 'react';
import {AgGridReact} from 'ag-grid-react';
import type {AgGridReactProps} from 'ag-grid-react';
import type {GridApi, GridReadyEvent} from 'ag-grid-community';
import {gridTheme} from './gridTheme';
import './agGrid.scss';

// long enough that the tooltip stays out of the way while the eye travels over the rows
const TOOLTIP_SHOW_DELAY = 300;

// as tight as a heading reads at Gaia's body size
const HEADER_HEIGHT = 40;

/**
 * ag-grid's own event for the vertical scrollbar appearing or hiding. It is dispatched on the same
 * bus as the published events and reaches api.addEventListener the same way, but it is not part of
 * the published API - which is all the cast below says. If a later ag-grid renames it, the columns
 * go back to being a scrollbar out (see keepColumnsFitted), nothing worse.
 */
const SCROLL_VISIBILITY_CHANGED = 'scrollVisibilityChanged';

type EventRegistration = (eventType: string, listener: () => void) => void;

/**
 * Keeps the flex columns filling the grid as its vertical scrollbar comes and goes.
 *
 * A flex column is sized against the viewport less that scrollbar, and ag-grid re-runs the sizing
 * when the viewport element resizes - not when the scrollbar alone appears or hides, which is what
 * a page of rows landing (or a search narrowing the list) does. Left alone the columns stay sized
 * for the state before: a scrollbar too wide and a horizontal scrollbar appears, a scrollbar too
 * narrow and a gap opens after the last column.
 *
 * Handing the column state straight back re-runs the sizing, which reads the width as it is now.
 * The columns already fitting makes it a no-op, and the event only fires when the scrollbar
 * actually changes, so this settles rather than feeds itself. A column the user has resized keeps
 * its width: that one has no flex left to re-run, and the state carries the width it was given.
 */
function keepColumnsFitted(api: GridApi) {
    const addEventListener = api.addEventListener as unknown as EventRegistration;
    addEventListener(SCROLL_VISIBILITY_CHANGED, () => {
        if (!api.isDestroyed()) {
            api.applyColumnState({state: api.getColumnState()});
        }
    });
}

export interface AppGridProps<TRow> extends AgGridReactProps<TRow> {
    /**
     * Extra class on the scrolling container, for the page's own cell rules. The container
     * always carries `app-grid` as well, which is what the shared stylesheet is written against.
     */
    className?: string;
    dataCy?: string;
    /** The measured height, in pixels - see useAvailableHeight. The stylesheet's own height stands without it. */
    height?: number | null;
    /** The container element, for a page that measures it or drives focus inside it. */
    containerRef?: (element: HTMLDivElement | null) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

/**
 * An ag-grid wearing this app's Gaia theme, in the scrolling container the shared stylesheet
 * expects (see agGrid.scss).
 *
 * Everything below is a default: a page passing the same prop overrides it, since its props
 * are spread after these.
 */
export const AppGrid = <TRow,>({
                                   className,
                                   dataCy,
                                   height,
                                   containerRef,
                                   onKeyDown,
                                   onGridReady,
                                   ...gridProps
                               }: AppGridProps<TRow>) => {

    // a page's own onGridReady is kept, rather than overridden, so it still hears the grid arrive
    const gridReady = useCallback((event: GridReadyEvent<TRow>) => {
        keepColumnsFitted(event.api);
        onGridReady?.(event);
    }, [onGridReady]);

    return (
        <div className={className ? `app-grid ${className}` : 'app-grid'}
             data-cy={dataCy}
             ref={containerRef}
             style={height ? {height} : undefined}
             onKeyDown={onKeyDown}>
            <AgGridReact<TRow>
                theme={gridTheme}
                headerHeight={HEADER_HEIGHT}
                defaultColDef={{resizable: true}}
                // the rows are the tab stops, and the cells are read as plain text
                suppressCellFocus={true}
                // the tab order walks the rows in display order, so the DOM has to be in that
                // order too - for the reader as much as for the fallback the browser provides
                ensureDomOrder={true}
                tooltipShowDelay={TOOLTIP_SHOW_DELAY}
                // the values are readable in the cell; only a clipped one needs spelling out
                tooltipShowMode="whenTruncated"
                domLayout="normal"
                {...gridProps}
                onGridReady={gridReady}
            />
        </div>
    );
};

export default AppGrid;
